import { useMemo } from "react";
import { ResponsiveContainer, Tooltip, Treemap } from "recharts";
import {
  ChartEmptyState,
  ChartErrorState,
  ChartLoadingState,
} from "../ChartStates";
import {
  BAR_FILL,
  CHART_PALETTE_LIGHT,
  MARK_CORNER_RADIUS,
  MARK_GAP_PX,
  MARK_STROKE_WIDTH,
  formatCompact,
  formatNumber,
  markGlow,
} from "../chartUtils";

/**
 * Treemap: part-to-whole where the parts are too many for a ring and their
 * labels need room. Area encodes magnitude, so the eye reads rank before it
 * reads any number.
 *
 * Labels are dropped from tiles too small to hold them rather than clipped
 * mid-word — a half-word is worse than no word, and the tooltip still carries
 * the full name and value.
 */
export interface TreeMapNode {
  name: string;
  value?: number;
  /** Per-tile fill. Falls back to the palette, cycled by position. */
  color?: string;
}

export interface TreeMapProps {
  /** Rows to plot. Non-arrays and empty arrays resolve to the empty state. */
  data: TreeMapNode[];
  height?: number;
  /** Replaces the default categorical hues. Assigned by position, cycled. */
  palette?: readonly string[];
  /**
   * Tile label colour. Defaults to the tile's OWN hue, which is the only
   * colour guaranteed to read against an unfilled tile — the label sits on
   * the card surface, not on a fill, so it takes the same colour as the
   * outline around it. Override only to force one ink across every tile.
   */
  labelColor?: string;
  loading?: boolean;
  /** Fetch failure. Takes precedence over every other state. */
  error?: string | Error | null;
  onRetry?: () => void;
  emptyMessage?: string;
  /** Value shown on the tile. Defaults to compact notation (1.2K). */
  valueFormatter?: (value: number) => string;
  /** Value shown in the tooltip. Defaults to the full, grouped number. */
  tooltipValueFormatter?: (value: number) => string;
  /** Noun for the tooltip's value row, e.g. "Findings". */
  valueLabel?: string;
  className?: string;
  ariaLabel?: string;
}

const DEFAULT_LABEL_COLOR = "#1f2937";

const LABEL_FONT = 12;
/** Below this the label is no longer worth reading -- drop it instead. */
const MIN_LABEL_FONT = 9;
const VALUE_FONT = 14;
/** Average glyph width as a fraction of font size, for the UI font. */
const CHAR_RATIO = 0.56;
const LINE_GAP = 3;
const TILE_PAD = 8;

/** Greedy word wrap against a per-line character budget. */
function wrapLabel(name: string, maxChars: number): string[] {
  const lines: string[] = [];
  let line = "";
  name
    .split(/\s+/)
    .filter(Boolean)
    .forEach((word) => {
      const next = line ? `${line} ${word}` : word;
      // A word longer than the budget still gets its own line; the font
      // shrink below is what actually makes it fit.
      if (next.length <= maxChars || !line) {
        line = next;
      } else {
        lines.push(line);
        line = word;
      }
    });
  if (line) lines.push(line);
  return lines;
}

interface TreemapCellProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
  /** Nesting level. Recharts draws a depth-0 root behind the leaves. */
  depth?: number;
  name?: string;
  value?: number;
  color?: string;
  labelColor?: string;
  valueFormatter?: (value: number) => string;
}

function TreemapCell({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  depth = 1,
  name = "",
  value = 0,
  color,
  labelColor,
  valueFormatter = formatCompact,
}: TreemapCellProps) {
  // Recharts lays a root node behind the leaves, spanning the whole plot and
  // carrying the grand total. Solid tiles used to paint over it; unfilled ones
  // do not, so it surfaced as a stray total (a bare "62") floating behind the
  // real tiles, plus a border around the entire chart. It is structure, not
  // data -- skip it.
  if (depth === 0) return null;
  // SVG text does not wrap or shrink on its own, so a tile narrower than its
  // name used to paint straight over its neighbour. Wrap the name to the tile,
  // then shrink the type until the longest line genuinely fits; if even the
  // floor font is too big, drop the label and let the tooltip carry the name.
  // Recharts lays tiles edge to edge, so neighbouring outlines touch and read
  // as one shape. Inset each by half the gap on every side; two neighbours
  // then contribute half each and the space between them lands on MARK_GAP_PX,
  // matching the donut segments and the gauge arcs.
  const inset = MARK_GAP_PX / 2;
  const tileX = x + inset;
  const tileY = y + inset;
  const tileW = Math.max(0, width - MARK_GAP_PX);
  const tileH = Math.max(0, height - MARK_GAP_PX);

  const availW = tileW - TILE_PAD * 2;
  const availH = tileH - TILE_PAD;

  const lines =
    availW > 0
      ? wrapLabel(name, Math.max(1, Math.floor(availW / (LABEL_FONT * CHAR_RATIO))))
      : [];
  const longest = lines.reduce((n, l) => Math.max(n, l.length), 0);
  const fontSize = longest > 0 ? Math.min(LABEL_FONT, availW / (longest * CHAR_RATIO)) : 0;
  const lineHeight = fontSize + LINE_GAP;
  const labelHeight = lines.length * lineHeight;

  const showLabel = fontSize >= MIN_LABEL_FONT && availH >= labelHeight;
  const valueBlock = VALUE_FONT + 4;
  const showValue =
    availW >= VALUE_FONT * 2 && availH >= (showLabel ? labelHeight : 0) + valueBlock;

  // Centred on the INSET tile, not the raw cell, so the text stays centred
  // within the visible outline rather than within the gap around it.
  const blockHeight = (showLabel ? labelHeight : 0) + (showValue ? valueBlock : 0);
  const blockTop = tileY + (tileH - blockHeight) / 2;
  const centerX = tileX + tileW / 2;

  // The tile is outlined, not filled, matching the bars: its own hue on the
  // edge plus a glow of the same colour. The label therefore wears that hue
  // too -- the dark ink it used before was only legible against a solid fill.
  const ink = labelColor ?? color ?? DEFAULT_LABEL_COLOR;

  return (
    <g>
      <rect
        x={tileX}
        y={tileY}
        width={tileW}
        height={tileH}
        rx={MARK_CORNER_RADIUS}
        ry={MARK_CORNER_RADIUS}
        style={{
          fill: BAR_FILL,
          stroke: color,
          strokeWidth: MARK_STROKE_WIDTH,
          filter: color ? markGlow(color) : undefined,
        }}
      />
      {showLabel
        ? lines.map((line, i) => (
            <text
              key={line}
              x={centerX}
              y={blockTop + i * lineHeight + lineHeight / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={ink}
              fontSize={fontSize}
              fontWeight={600}
            >
              {line}
            </text>
          ))
        : null}
      {showValue ? (
        // The name wears the tile's hue; the count wears plain ink. A figure
        // is read, not identified, and at this weight a saturated numeral on
        // an unfilled tile is the least legible thing on the chart.
        <text
          x={centerX}
          y={blockTop + (showLabel ? labelHeight : 0) + valueBlock / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="var(--foreground)"
          fontSize={VALUE_FONT}
          fontWeight={700}
        >
          {valueFormatter(value)}
        </text>
      ) : null}
    </g>
  );
}

export function TreeMap({
  data,
  height = 270,
  palette = CHART_PALETTE_LIGHT,
  labelColor,
  loading,
  error,
  onRetry,
  emptyMessage = "No data available",
  valueFormatter = formatCompact,
  tooltipValueFormatter = formatNumber,
  valueLabel = "Value",
  className,
  ariaLabel,
}: TreeMapProps) {
  // Recharts mutates the array it is handed while laying tiles out, so it
  // always gets a fresh copy.
  const nodes = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    return rows.map((d, i) => ({
      name: d.name,
      value: d.value ?? 0,
      color: d.color ?? palette[i % palette.length],
    }));
  }, [data, palette]);

  const errorMessage = error instanceof Error ? error.message : error;
  if (errorMessage) {
    return <ChartErrorState height={height} message={errorMessage} onRetry={onRetry} />;
  }
  if (loading) return <ChartLoadingState height={height} shape="cartesian" />;
  // A zero-total treemap has no area to divide, so recharts draws nothing.
  if (!nodes.length || nodes.every((n) => n.value <= 0)) {
    return <ChartEmptyState height={height} message={emptyMessage} shape="cartesian" />;
  }

  return (
    <div className={className} style={{ height }} aria-label={ariaLabel} role="img">
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={nodes}
          dataKey="value"
          nameKey="name"
          isAnimationActive={false}
          content={
            <TreemapCell labelColor={labelColor} valueFormatter={valueFormatter} />
          }
        >
          <Tooltip
            cursor={{ fill: "transparent" }}
            formatter={(value: number) => [tooltipValueFormatter(value), valueLabel]}
            contentStyle={{
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              color: "var(--popover-foreground)",
              fontSize: 12,
            }}
            // The tiles are outline-only, so their fill is transparent and
            // recharts would ink the tooltip row with it -- invisible text.
            labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
            itemStyle={{ color: "var(--popover-foreground)" }}
          />
        </Treemap>
      </ResponsiveContainer>
    </div>
  );
}
